"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _hopsSdkJs = require("@hps/hops-sdk-js");
var _moment = _interopRequireDefault(require("moment"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _TicketJourneyTypeToggleButton = _interopRequireDefault(require("./TicketJourneyTypeToggleButton.js"));
var _Button = _interopRequireDefault(require("../Components/Button.js"));
var _Counter = _interopRequireDefault(require("../Components/Counter.js"));
var _DatePicker = _interopRequireDefault(require("../Components/DatePicker.js"));
var _Div = _interopRequireDefault(require("../Components/Div.js"));
var _Divider = _interopRequireDefault(require("../Components/Divider.js"));
var _Flex = _interopRequireDefault(require("../Components/Flex.js"));
var _IconButton = _interopRequireDefault(require("../Components/IconButton.js"));
var _Link = _interopRequireDefault(require("../Components/Link.js"));
var _Paper = _interopRequireDefault(require("../Components/Paper.js"));
var _Select = _interopRequireDefault(require("../Components/Select.js"));
var _String = _interopRequireDefault(require("../Components/String.js"));
var _Clear = _interopRequireDefault(require("@mui/icons-material/Clear"));
var _Search = _interopRequireDefault(require("@mui/icons-material/Search"));
var _SwapVert = _interopRequireDefault(require("@mui/icons-material/SwapVert"));
var _TicketBuilderModule = _interopRequireDefault(require("./TicketBuilder.module.scss"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Ticket builder UI
 * 
 * @package HOPS
 * @subpackage Tickets
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */class TicketBuilder extends _react.default.PureComponent {
  /**
   * Selection field value changed.
   * 
   * @param {mixed} value
   * @param {String} name
   * @return {void}
   */
  handleChange = (value, name) => {
    this.props.onChange({
      [name]: value
    });
  };

  /**
   * Travel date field value changed.
   * 
   * @param {mixed} value
   * @param {String} name
   * @return {void}
   */
  handleChangeTravelDate = (value, name) => {
    this.props.onChange({
      [name]: value?.format("YYYY-MM-DD")
    });
  };

  /**
   * Journey type changed.
   *
   * @param {Integer} JourneyType
   * @return {void}
   */
  handleChangeJourneyType = JourneyType => {
    const state = {
      JourneyType
    };

    /**
     * Changed from Ranger to a different journey type
     * (Note this is not the same as an "Open" journey which has no fixed departure time)
     *
     * We could end up with the departure and destination being 
     * the same depending on the user's previous selections.
     *
     * We reset the two to be different now.
     */
    if (this.props.selection.JourneyType === _hopsSdkJs.TicketJourneyTypes.values.Ranger) {
      const tpAId = this.props.selection.TimingPointA?.Id;
      const tpBId = this.props.selection.TimingPointB?.Id;
      if (tpAId && tpBId && tpAId === tpBId) {
        let i = -1;
        let tpB;
        const tpBs = [this.props.timingPoints.find(tp => tp.DefaultB), this.props.timingPoints[this.props.timingPoints.length - 1], this.props.timingPoints.find(tp => tp.Id !== tpAId) || null];
        while (!tpB || tpAId === tpB.Id) {
          i++;
          tpB = tpBs[i];
        }
        state.TimingPointB = tpB;
      }
    }

    /**
     * Changing to Circular
     * A round-trip starting at an intermediate station, travelling to a second station, then coming back past the first station to a third station, then back to the starting station.
     *
     * We adjust the selected timing points to make a valid selection.
     *
     * (If the railway is A - B - C, the journey must be B - A - C, or 
     * it could also be B - C - A).
     */
    if (JourneyType === _hopsSdkJs.TicketJourneyTypes.values.Circular) {
      state.TimingPointA = this.props.timingPoints.find(tp => tp.DefaultC) || this.props.timingPoints[1];
      state.TimingPointB = this.props.timingPoints.find(tp => tp.DefaultA) || this.props.timingPoints[0];
      state.TimingPointC = this.props.timingPoints.find(tp => tp.DefaultB) || this.props.timingPoints[this.props.timingPoints.length - 1];
    }
    this.props.onChange(state);
  };

  /**
   * Type count changed.
   *
   * @param {Integer} count
   * @param {String} type Type ID
   * @return {void}
   */
  handleChangeTypeCount = (count, type) => {
    const typeId = parseInt(type);
    if (this.props.onChangeTypeCount) {
      this.props.onChangeTypeCount({
        ...(this.props.selectionTypeCounts || this.props.selection.TypeCounts),
        [typeId]: count
      });
    } else {
      this.handleChange({
        ...this.props.selection.TypeCounts,
        [typeId]: count
      }, "TypeCounts");
    }
  };

  /**
   * Switching timing points.
   * 
   * @return {void}
   */
  handleSwitchTimingPoints = () => {
    const {
      TimingPointA,
      TimingPointB,
      TimingPointC,
      JourneyType
    } = this.props.selection;
    if (parseInt(JourneyType) !== _hopsSdkJs.TicketJourneyTypes.values.Circular) {
      this.props.onChange({
        TimingPointA: TimingPointB,
        TimingPointB: TimingPointA
      });
    } else {
      // hops#818 circular journeys switch the opposite way around the circle
      this.props.onChange({
        TimingPointB: TimingPointC,
        TimingPointC: TimingPointB
      });
    }
  };

  /**
   * Compute journey options to hide based on our timing points list.
   *
   * @param {Array} initial optional Force some exclusions
   * @return {Array}
   */
  getHiddenJourneyOptions(initial = []) {
    // Exclude types that aren't in the Enabled Journey Types list
    const disabledJourneyTypes = Object.values(_hopsSdkJs.TicketJourneyTypes.values).filter(journeyType => this.props.enabledJourneyTypes?.indexOf(journeyType) === -1);
    initial = initial.concat(disabledJourneyTypes);

    // Exclude Circular journeys if there are less than 3 timing points
    if (this.props.timingPoints?.length < 3) {
      initial.push(_hopsSdkJs.TicketJourneyTypes.values.Circular);
    }

    // Exclude Single and Return journeys if there is only 1 timing point
    if (this.props.timingPoints?.length === 1) {
      initial.push(_hopsSdkJs.TicketJourneyTypes.values.Single);
      initial.push(_hopsSdkJs.TicketJourneyTypes.values.Return);
    }
    return initial;
  }

  /**
   * Render.
   * 
   * @return {ReactNode}
   */
  render() {
    const rowClasses = this.rowCssClassesString;
    const RootComponent = !this.props.noPaper ? _Paper.default : _Flex.default;
    const RootComponentProps = !this.props.noPaper ? {
      height: "100%",
      PaperProps: {
        ref: this.props.rootRef
      },
      variant: this.props.elevated ? "elevation" : "outlined"
    } : {};
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(RootComponent, {
      ...RootComponentProps,
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
          alignItems: "center",
          columnar: true,
          hidden: this.props.hideHeader,
          justifyContent: "space-between",
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
            noFlex: true,
            str: "Ticket Builder",
            variant: "h6"
          }), this.props.withResetButton && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Button.default, {
            endIcon: _Clear.default,
            label: "Reset",
            onClick: this.props.onReset,
            size: "small",
            variant: "text"
          })]
        }), !this.props.hideJourneyTypes && this.props.useRadios && this.renderJourneyRadio(), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
          hidden: this.props.hideTravelDate && this.props.hideTimingPoints && (this.props.hideFareTypes || !this.props.withTypeFields) && !this.props.withClassSelect,
          my: this.props.selectorsMargin,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_Div.default, {
            className: `${rowClasses} ${this.props.hideTravelDate ? _TicketBuilderModule.default.row__hidden : ""}`.trim(),
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
              noFlex: true,
              str: "Date"
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_DatePicker.default, {
              className: _TicketBuilderModule.default.datePicker,
              clearable: this.props.dateClearable,
              color: this.props.color,
              dayStyles: this.props.dayStyles,
              disabled: this.props.disabled,
              disablePast: true,
              focusFirstSelectableDay: this.props.focusFirstOperatingDay,
              format: "DD/MM/YYYY",
              name: "Date",
              onChange: this.handleChangeTravelDate,
              selectableDays: this.props.operatingDays,
              slotProps: {
                textField: {
                  variant: "standard"
                },
                toolbar: {
                  hidden: true
                }
              },
              value: new _moment.default(this.props.selection.Date),
              variant: "responsive"
            })]
          }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Div.default, {
            className: `${rowClasses} ${this.props.hideTimingPoints ? _TicketBuilderModule.default.row__hidden : ""}`.trim(),
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
              noFlex: true,
              str: "Depart"
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Select.default, {
              color: this.props.color,
              disabled: this.props.disabled,
              name: "TimingPointA",
              onChange: this.handleChange,
              options: this.tpOptionsA,
              removeLabelOption: true,
              value: this.props.selection.TimingPointA?.Id,
              variant: "standard"
            }), !this.isRangerJourney && this.renderTpSwitchIcon(), !this.isRangerJourney && this.renderTimingPointB(), this.isCircularJourney && this.renderTimingPointC()]
          }), this.props.withClassSelect && this.renderClasses(), this.props.withTypeFields && !this.props.hideFareTypes && this.renderFareTypes()]
        }), !this.props.hideJourneyTypes && !this.props.useRadios && this.renderJourneySelect(), this.props.withTypeFields && this.renderTypes(), this.props.withSearchButton && this.renderSearchButton(), this.props.withViewResultsLink && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Link.default, {
          color: this.props.color,
          label: `View Results ${String.fromCharCode(8595)}`,
          onClick: this.props.onViewResults
        })]
      })
    });
  }

  /**
   * Render the class selector.
   *
   * @return {ReactNode}
   */
  renderClasses() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Div.default, {
      className: this.rowCssClassesString,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
        noFlex: true,
        str: "Class"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Select.default, {
        color: this.props.color,
        disabled: this.props.disabled,
        name: "Class",
        onChange: this.handleChange,
        options: this.classOptions,
        removeLabelOption: true,
        value: this.props.selection.Class?.Id,
        variant: "standard"
      })]
    });
  }

  /**
   * Render the fare type selector.
   * 
   * @return {ReactNode}
   */
  renderFareTypes() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Div.default, {
      className: this.rowCssClassesString,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
        noFlex: true,
        str: "Fare Type"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Select.default, {
        color: this.props.color,
        disabled: this.props.disabled,
        name: "FareType",
        onChange: this.handleChange,
        options: this.fareTypeOptions,
        removeLabelOption: true,
        value: this.props.selection.FareType?.Id,
        variant: "standard"
      })]
    });
  }

  /**
   * Render the journey type radio control.
   * 
   * @return {ReactNode}
   */
  renderJourneyRadio() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_TicketJourneyTypeToggleButton.default, {
        disabled: this.props.disabled,
        hiddenOptions: this.getHiddenJourneyOptions(),
        onChange: this.handleChangeJourneyType,
        value: this.props.selection.JourneyType
      }), !this.props.hideJourneyTypeDescription && /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
        noFlex: false,
        str: this.journeyTypeDescription,
        variant: "caption",
        color: "textSecondary"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Divider.default, {})]
    });
  }

  /**
   * Get selected journey type description.
   * 
   * @return {Object}
   */
  get journeyTypeDescription() {
    const journeyDescriptions = {
      [_hopsSdkJs.TicketJourneyTypes.values.Single]: "A journey from your starting station to your ending station and NO journey back. Please note your journey will end at a different place to where it starts. If you wish to come back, please select 'Return'.",
      [_hopsSdkJs.TicketJourneyTypes.values.Return]: "A journey from your starting station to your ending station and back.",
      [_hopsSdkJs.TicketJourneyTypes.values.Circular]: "A round-trip starting at an intermediate station, travelling to a second station, then coming back past the first station to a third station, then back to the starting station.",
      [_hopsSdkJs.TicketJourneyTypes.values.Ranger]: "A ticket valid all day."
    };
    return journeyDescriptions[this.props.selection.JourneyType];
  }

  /**
   * Render the journey type select control.
   *
   * @return {ReactNode}
   */
  renderJourneySelect() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Div.default, {
      className: this.rowCssClassesString,
      mb: 1,
      mt: -1,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
        noFlex: true,
        str: "Journey"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Select.default, {
        color: this.props.color,
        disabled: this.props.disabled,
        name: "JourneyType",
        onChange: this.handleChange,
        options: this.journeyTypeOptions,
        removeLabelOption: true,
        value: this.props.selection.JourneyType,
        variant: "standard"
      })]
    });
  }

  /**
   * Render the search button.
   * 
   * @return {ReactNode}
   */
  renderSearchButton() {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Button.default, {
      className: _TicketBuilderModule.default.search,
      disabled: this.props.disabled,
      label: "Search",
      onClick: this.props.onSearch,
      startIcon: _Search.default,
      size: "large",
      variant: "contained"
    });
  }

  /**
   * Render timing point B.
   * 
   * @return {ReactNode}
   */
  renderTimingPointB() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
        noFlex: true,
        str: `Destination${this.isCircularJourney ? " 1" : ""}`
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Select.default, {
        color: this.props.color,
        disabled: this.props.disabled,
        name: "TimingPointB",
        onChange: this.handleChange,
        options: this.tpOptionsB,
        removeLabelOption: true,
        value: this.props.selection.TimingPointB?.Id,
        variant: "standard"
      })]
    });
  }

  /**
   * Render timing point C.
   * 
   * @return {ReactNode}
   */
  renderTimingPointC() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
        noFlex: true,
        str: "Destination 2"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Select.default, {
        color: this.props.color,
        disabled: this.props.disabled,
        name: "TimingPointC",
        onChange: this.handleChange,
        options: this.tpOptionsC,
        removeLabelOption: true,
        value: this.props.selection.TimingPointC?.Id,
        variant: "standard"
      })]
    });
  }

  /**
   * Render the timing point switch icon button.
   * 
   * @return {ReactNode}
   */
  renderTpSwitchIcon() {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Flex.default, {
      alignSelf: "center",
      className: `${_TicketBuilderModule.default.tpSwitchIcon} ${this.isCircularJourney ? _TicketBuilderModule.default.__hasTpC : ""}`.trim(),
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_IconButton.default, {
        color: this.props.color,
        disabled: this.props.disabled,
        icon: _SwapVert.default,
        label: "Swap",
        onClick: this.handleSwitchTimingPoints,
        size: "small"
      })
    });
  }

  /**
   * Render the ticket count selection for each type.
   *
   * @return {ReactNode}
   */
  renderTypes() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
      children: [(!(this.props.hideFareTypes || !this.props.withTypeFields) || this.props.withClassSelect || !this.props.hideTimingPoints || !this.props.hideTravelDate) && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Divider.default, {}), this.props.types?.map((type, key) => {
        return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Div.default, {
          className: `${this.rowCssClassesString} ${_TicketBuilderModule.default.row__counters}`,
          mt: key === 0 ? 1 : 0,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
            gap: 0,
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
              noFlex: true,
              str: type.Name
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
              color: "textSecondary",
              noFlex: true,
              str: type.People > 1 && !this.props.hideTicketTypePeopleCoveredCounts ? `${type.People} People` : undefined,
              variant: "overline"
            })]
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Counter.default, {
            FlexProps: this.constructor.counterFlexProps,
            buttonColor: this.props.color,
            buttonSize: "medium",
            disabled: this.props.disabled,
            gap: 1.5,
            min: 0,
            name: type.Id?.toString(),
            onChange: this.handleChangeTypeCount,
            value: (this.props.selectionTypeCounts || this.props.selection.TypeCounts)[type.Id] || 0
          })]
        }, key);
      }), !this.props.types?.length && /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
        classNameTypography: _TicketBuilderModule.default.noTicketTypesMessage,
        color: "textSecondary",
        noFlex: true,
        str: this.props.noTicketTypesMessage
      })]
    });
  }

  /**
   * Get class options.
   * 
   * @return {Array}
   */
  get classOptions() {
    return this.props.classes.map(cls => {
      return {
        id: cls.Id,
        label: cls.Name,
        value: cls
      };
    });
  }

  /**
   * Get fare type options.
   * 
   * @return {Array}
   */
  get fareTypeOptions() {
    return this.props.fareTypes.map(ft => {
      return {
        id: ft.Id,
        label: ft.Name,
        value: ft
      };
    });
  }

  /**
   * Get journey type options.
   * 
   * @return {Array}
   */
  get journeyTypeOptions() {
    const hiddenOptions = this.getHiddenJourneyOptions();
    return Object.keys(_hopsSdkJs.TicketJourneyTypes.values).map(journey => {
      const value = _hopsSdkJs.TicketJourneyTypes.values[journey];
      return {
        id: value,
        label: _hopsSdkJs.TicketJourneyTypes.getLabel(value),
        value,
        hidden: hiddenOptions.includes(value)
      };
    });
  }

  /**
   * Get timing point A options.
   * 
   * @return {Array|undefined}
   */
  get tpOptionsA() {
    return this.props.timingPoints.map(tp => {
      return {
        id: tp.Id,
        label: tp.Name,
        value: tp
      };
    });
  }

  /**
   * Get timing point B options.
   * 
   * @return {Array|undefined}
   */
  get tpOptionsB() {
    return this.props.timingPoints?.map(tp => {
      return {
        id: tp.Id,
        label: tp.Name,
        value: tp
      };
    });
  }

  /**
   * Get timing point C options.
   *
   * @return {Array|undefined}
   */
  get tpOptionsC() {
    return this.props.timingPoints?.map(tp => {
      return {
        id: tp.Id,
        label: tp.Name,
        value: tp
      };
    });
  }

  /**
   * Get whether a Ranger journey is selected.
   * 
   * @return {Boolean}
   */
  get isRangerJourney() {
    return parseInt(this.props.selection.JourneyType) === _hopsSdkJs.TicketJourneyTypes.values.Ranger;
  }

  /**
   * Get whether a circular journey is selected.
   * 
   * @return {Boolean}
   */
  get isCircularJourney() {
    return parseInt(this.props.selection.JourneyType) === _hopsSdkJs.TicketJourneyTypes.values.Circular;
  }

  /**
   * Get the CSS class names string to apply to control rows.
   *
   * @return {String}
   */
  get rowCssClassesString() {
    return `${_TicketBuilderModule.default.row} ${_TicketBuilderModule.default.row__main} ${this.props.hideTimingPoints ? _TicketBuilderModule.default.tpsHidden : ""}`.trim();
  }

  /**
   * `FlexProps` for `Counter` components
   * 
   * @type {Object}
   */
  static counterFlexProps = {
    className: _TicketBuilderModule.default.counters
  };
  static propTypes = {
    classes: _propTypes.default.array,
    color: _propTypes.default.oneOf(["primary", "secondary"]),
    disabled: _propTypes.default.bool,
    elevated: _propTypes.default.bool,
    enabledJourneyTypes: _propTypes.default.array,
    fareTypes: _propTypes.default.array,
    hideFareTypes: _propTypes.default.bool,
    hideHeader: _propTypes.default.bool,
    hideJourneyTypes: _propTypes.default.bool,
    hideJourneyTypeDescription: _propTypes.default.bool,
    hideTicketTypePeopleCoveredCounts: _propTypes.default.bool,
    hideTimingPoints: _propTypes.default.bool,
    hideTravelDate: _propTypes.default.bool,
    noPaper: _propTypes.default.bool,
    noTicketTypesMessage: _propTypes.default.string,
    onChange: _propTypes.default.func,
    onChangeTypeCount: _propTypes.default.func,
    onReset: _propTypes.default.func,
    onSearch: _propTypes.default.func,
    onViewResults: _propTypes.default.func,
    operatingDays: _propTypes.default.arrayOf(_propTypes.default.string),
    selection: _propTypes.default.object,
    selectionTypeCounts: _propTypes.default.object,
    selectorsMargin: _propTypes.default.number,
    rootRef: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.shape({
      current: _propTypes.default.instanceOf(Element)
    })]),
    timingPoints: _propTypes.default.array,
    types: _propTypes.default.array,
    useRadios: _propTypes.default.bool,
    withClassSelect: _propTypes.default.bool,
    withResetButton: _propTypes.default.bool,
    withSearchButton: _propTypes.default.bool,
    withTypeFields: _propTypes.default.bool,
    withViewResultsLink: _propTypes.default.bool
  };
  static defaultProps = {
    color: "primary",
    disabled: false,
    elevated: true,
    hideFareTypes: false,
    hideHeader: false,
    hideJourneyTypes: false,
    hideJourneyTypeDescription: false,
    hideTicketTypePeopleCoveredCounts: false,
    hideTimingPoints: false,
    hideTravelDate: false,
    noPaper: false,
    noTicketTypesMessage: "No tickets available.",
    selectorsMargin: 1,
    useRadios: true,
    withClassSelect: false,
    withResetButton: false,
    withSearchButton: false,
    withTypeFields: true,
    withViewResultsLink: false
  };
}
var _default = exports.default = TicketBuilder;